import { useCallback, useEffect, useState } from "react";
import { useAlert, useAuth, usePayment } from "src/hooks/context";
import Button from "@joinTrilo/ui/atoms/Button/Button";
import Tab from "@joinTrilo/ui/atoms/Tab/Tab";
import { Email } from "./Email";
import { StyledFormContainer, StyledFormWrapper } from "./Auth.styled";
import { Password } from "./Password";
import { Signup, SignupStateProps } from "./Signup";

import { useLookup } from "src/hooks/auth/useLookup";
import { useLogin } from "src/hooks/auth/useLogin";
import { useSignup } from "src/hooks/auth/useSignup";

import { setAuthCookies } from "../../utils/cookies";
import { GuestCheckout } from "./GuestCheckout";
import { useCredentials } from "src/hooks/useCredentials/useCredentials.callback";

export const Auth = () => {
    const { authState, updateAuthState } = useAuth();
    const { paymentState } = usePayment();
    const { authCredentials } = useCredentials();
    const { lookup } = useLookup();
    const { login } = useLogin();
    const { signup } = useSignup();

    const [email, setEmail] = useState<string | undefined>(undefined);
    const [password, setPassword] = useState<string | undefined>(undefined);
    const [signupProps, setSignupProps] = useState<
        SignupStateProps | undefined
    >(undefined);
    const [signupError, setSignupError] = useState<string | undefined>(
        undefined
    );
    const [userWantsToLogin, setUserWantsToLogin] = useState<boolean>(false);
    const [showLogin, setShowLogin] = useState(false);
    const [showSignup, setShowSignup] = useState(false);
    const [isButtonLoading, setIsButtonLoading] = useState(false);

    const { updateAlertState } = useAlert();

    async function checkIsAlreadyRegistered(email: string) {
        const { isSignedUp } = await lookup(email);
        return isSignedUp;
    }

    const submitAnon = useCallback(async () => {
        updateAuthState({
            isGuest: null,
            isLoggedIn: null,
        });
        const response = await authCredentials({
            identityId: authState?.identityId ?? undefined,
        });
        console.log("[GuestCheckout] authCredentials", { response });
        setAuthCookies({
            identityId: response?.identityId ?? undefined,
            refreshToken: response?.refreshToken ?? undefined,
        });
        console.log("[GuestCheckout] setAuthCookies", {
            identityId: response?.identityId ?? undefined,
            refreshToken: response?.refreshToken ?? undefined,
        });
        updateAuthState({
            ...response,
            intercomConfig: response?.intercomConfig ?? undefined,
            identityId: response?.identityId ?? undefined,
            narwhalId: response?.narwhalId ?? undefined,
            credentials:
                response?.credentials && response.credentials !== null
                    ? JSON.parse(response.credentials)
                    : undefined,
            isSignupComplete:
                response?.isSignupComplete === undefined ||
                response?.isSignupComplete === null
                    ? undefined
                    : response.isSignupComplete !== "false",
            isLoggedIn: false,
            isGuest: true,
        });
    }, [updateAuthState, authCredentials, authState?.identityId]);

    useEffect(() => {
        console.log("[email]", email);
        setShowLogin(false);
        setShowSignup(false);
    }, [email]);

    useEffect(() => {
        if (
            paymentState.recipient?.features?.guestCheckout === "ALWAYS_ON" &&
            !authState.isLoggedIn
        ) {
            submitAnon();
        }
    }, [
        paymentState.recipient?.features?.guestCheckout,
        authState.isLoggedIn,
        submitAnon,
    ]);

    const onSubmitEmail = async () => {
        console.log("[onsubmit email]", email);
        setIsButtonLoading(true);
        if (email) {
            const isAlreadyRegistered = await checkIsAlreadyRegistered(email);
            if (isAlreadyRegistered) {
                setShowLogin(true);
                setShowSignup(false);
            } else {
                setShowSignup(true);
                setShowLogin(false);
            }
        } else {
            updateAlertState({
                message: "Please enter a valid email address",
                type: "error",
                show: true,
            });
        }
        setIsButtonLoading(false);
    };
    const onSubmitLogin = async () => {
        setIsButtonLoading(true);
        if (password && email) {
            try {
                const result = await login({ email, password });
                if (result?.isTemporaryPassword) {
                    updateAuthState({
                        narwhalId: result.narwhalId,
                        identityId: result.identityId,
                        isLoggedIn: false,
                        isTemporaryPassword: true,
                        intercomConfig: result.intercomConfig ?? undefined,
                    });
                    setAuthCookies({
                        refreshToken: result.refreshToken,
                        identityId: result.identityId,
                    });
                } else {
                    updateAuthState({
                        narwhalId: result?.narwhalId,
                        identityId: result?.identityId,
                        isLoggedIn: true,
                        isTemporaryPassword: false,
                        intercomConfig: result?.intercomConfig ?? undefined,
                    });
                    setAuthCookies({
                        refreshToken: result?.refreshToken,
                        identityId: result?.identityId,
                    });
                }
            } catch (e: any) {
                updateAlertState({
                    message: "Incorrect credentials",
                    type: "error",
                    show: true,
                });
            }
        } else {
            updateAlertState({
                message: "Something went wrong",
                type: "error",
                show: true,
            });
        }
        setIsButtonLoading(false);
    };
    const onSubmitSignup = async () => {
        setIsButtonLoading(true);
        if (signupError) {
            updateAlertState({
                message: signupError,
                type: "error",
                show: true,
            });
        }
        if (signupProps && email) {
            const response = await authCredentials({});
            if (!response?.identityId) {
                updateAlertState({
                    message: "Could not create identity",
                    type: "error",
                    show: true,
                });
                return;
            }
            try {
                const result = await signup({
                    email,
                    password: signupProps.new_password,
                    cognitoIdentityId: response?.identityId,
                    firstName: signupProps.firstName,
                    lastName: signupProps.lastName,
                });
                updateAuthState({
                    narwhalId: result?.narwhalId,
                    identityId: result?.identityId,
                    isLoggedIn: true,
                });
                setAuthCookies({
                    refreshToken: result?.refreshToken,
                    identityId: result?.identityId,
                });
            } catch (e: any) {
                updateAlertState({
                    message: e.message,
                    type: "error",
                    show: true,
                });
            }
        }
        setIsButtonLoading(false);
    };

    if (
        paymentState.recipient?.features?.guestCheckout === "OPTIONAL" &&
        (!authState.narwhalId || authState.narwhalId === null) &&
        !userWantsToLogin
    ) {
        return (
            <GuestCheckout
                userWantsToLogin={setUserWantsToLogin}
                submitAnon={submitAnon}
            />
        );
    }

    if (authState.isTemporaryPassword) {
        return (
            <StyledFormContainer display="true">
                <p>
                    Your password is temporary. Please reset it before continuing.
                </p>
                <a href="https://auth.trilo.io/forgotpassword">
                    Reset your password
                </a>
            </StyledFormContainer>
        );
    }

    return (
        <>
            <StyledFormContainer display={"true"}>
                <Tab
                    tabs={[
                        {
                            key: "getYourReceipt",
                            text: showLogin
                                ? "🔐 Enter your password to log in"
                                : showSignup
                                    ? "🔊 Finish setting up"
                                    : "📧 Enter your email",
                        },
                    ]}
                />

                <StyledFormWrapper
                    noValidate={true}
                    id="auth"
                    onSubmit={(event) => {
                        event.preventDefault();
                        if (!showLogin && !showSignup) {
                            onSubmitEmail();
                        }
                        if (showLogin) {
                            onSubmitLogin();
                        }
                        if (showSignup) {
                            onSubmitSignup();
                        }
                    }}
                >
                    <Email setEmail={setEmail} />
                    {showLogin && <Password updatePassword={setPassword} />}
                    {showSignup && (
                        <Signup
                            updateSignup={(
                                signupProps: SignupStateProps | undefined,
                                errorMessage: string | undefined = undefined
                            ) => {
                                if (signupProps) {
                                    setSignupProps(signupProps);
                                }
                                setSignupError(errorMessage);
                            }}
                        />
                    )}
                    <Button
                        size="lg"
                        color="dark-turquoise"
                        isDisabled={!email && !password}
                        onClick={(event) => {
                            event.preventDefault();
                            if (!showLogin && !showSignup) {
                                onSubmitEmail();
                            }
                            if (showLogin) {
                                onSubmitLogin();
                            }
                            if (showSignup) {
                                onSubmitSignup();
                            }
                        }}
                        type="submit"
                        width="100%"
                        text="Continue"
                        isLoading={isButtonLoading}
                    />
                </StyledFormWrapper>
            </StyledFormContainer>
        </>
    );
};
